import {
  type QueryKey,
  useIsFetching,
  useQueryClient,
} from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

type RetryQueryAlertProps = {
  queryKey: QueryKey;
  message?: string;
  onRetry?: () => void;
};

export function RetryQueryAlert({
  queryKey,
  message,
  onRetry,
}: RetryQueryAlertProps) {
  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey }) > 0;
  const { t } = useTranslation('errors');

  const handleRetry = () => {
    queryClient.invalidateQueries({ queryKey });
    onRetry?.();
  };

  return (
    <div className='flex flex-col gap-y-3 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm'>
      <div className='flex items-center gap-x-2 font-medium'>
        <AlertCircle className='h-4 w-4' />
        <span>{message || 'Failed to load data'}</span>
      </div>
      <Button
        className='w-min bg-transparent text-destructive'
        disabled={isFetching}
        onClick={handleRetry}
        size='sm'
        type='button'
        variant='outline'
      >
        <RefreshCw className='mr-2 h-3 w-3' />
        {t('Try again')}
      </Button>
    </div>
  );
}
